import { type FC, useState, useEffect, type ReactNode, Children, useCallback } from 'react';

import { IconStyle } from '../Icons/Icons';
import CarouselButton from './CarouselButton';
import type { FiniteCarouselPropTypes } from './types/propTypes';
import styles from './Carousel.module.scss';
import { GUTTER_SIZE } from './types/enums';

const FiniteCarousel: FC<FiniteCarouselPropTypes> = ({
  children,
  currentSlide,
  hideButtons = false,
  page,
  carouselContainer,
  carouselItem,
  dotsToDisplay,
  gutterSize = GUTTER_SIZE.MEDIUM,
  isTileCarousel,
  setCurrentSlide,
  setPageOffset,
  resumeInterval,
}) => {
  // State
  const [hideLeftButton, setHideLeftButton] = useState(true);
  const [hideRightButton, setHideRightButton] = useState(dotsToDisplay === 1);
  const [childNodes, setChildNodes] = useState<ReactNode[]>(children ? [...Children.toArray(children)] : []);
  useEffect(() => {
    setHideRightButton(dotsToDisplay === 1);
  }, [dotsToDisplay]);
  // Effect for Initialization
  useEffect(() => {
    setChildNodes([
      ...Children.toArray(children), // current
    ]);
    setCurrentSlide({ slide: 0 });
    const element = carouselContainer.current;
    if (!element) {
      return undefined;
    }

    const onScroll = () => {
      setHideRightButton(element.scrollWidth - (element.scrollLeft + element.clientWidth) < 2);
      setHideLeftButton(element.scrollLeft === 0);
    };
    element.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      element.removeEventListener('scroll', onScroll);
    };
  }, [children, dotsToDisplay, carouselContainer, setCurrentSlide]);

  const goPrev = useCallback(() => {
    if (page === 0) {
      carouselContainer.current?.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    } else {
      setPageOffset(() => ({ page: Math.max((page ?? 0) - 1, 0) }));
    }
    resumeInterval?.();
  }, [page, dotsToDisplay]);

  const goNext = useCallback(() => {
    if (page === dotsToDisplay - 1) {
      const maxLeft = (carouselContainer.current?.scrollWidth ?? 0) - (carouselContainer.current?.clientWidth ?? 0);
      carouselContainer.current?.scrollTo({
        left: maxLeft,
        behavior: 'smooth',
      });
    } else {
      setPageOffset(() => ({
        page: Math.min((page ?? 0) + 1, dotsToDisplay - 1),
      }));
    }
    resumeInterval?.();
  }, [page, dotsToDisplay, resumeInterval, carouselContainer]);
  return (
    <>
      {!hideButtons && !hideLeftButton && Children.count(children) ? (
        <CarouselButton
          id="carousel-button-left"
          icon="chevron-left"
          iconStyle={IconStyle.SOLID}
          iconClass={styles['carousel-button-left'] as string}
          data-testid="carousel-button-left"
          clickHandler={goPrev}
        />
      ) : (
        <></>
      )}
      {childNodes.map((e, i) => (
        <div
          className={`${styles['carousel-item']} ${currentSlide.slide === i ? styles['active'] : ''} ${
            isTileCarousel ? styles[`gutter-${gutterSize}`] : ''
          }`}
          data-testid={`carousel-item-${i}`}
          ref={(el: HTMLDivElement) => (carouselItem.current[i] = el)}
          key={`node${+i}`}
          id={`carousel-item-${i}`}
          style={{
            minWidth: !isTileCarousel ? '100%' : 'auto',
          }}
        >
          {e}
        </div>
      ))}
      {!hideButtons && !hideRightButton && Children.count(children) ? (
        <CarouselButton
          id="carousel-button-right"
          icon="chevron-right"
          iconStyle={IconStyle.SOLID}
          data-testid="carousel-button-right"
          iconClass={styles['carousel-button-right'] as string}
          clickHandler={goNext}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default FiniteCarousel;
